/**
 * Dashboard slice - single source for dashboard API
 */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api/config';
import { DASHBOARD, ACTIVITY_LOGS } from '../../api/endpoints';

/** GET /dashboards/calls_counter_summary — params: date_from?, date_to?, team_id? */
export const getCallsCounterSummary = createAsyncThunk(
  'dashboard/getCallsCounterSummary',
  async (params = {}, { rejectWithValue }) => {
    try {
      const queryParams =
        params && typeof params === 'object' && Object.keys(params).length > 0
          ? { ...params }
          : undefined;
      const { data } = await api.get(DASHBOARD.CALLS_COUNTER_SUMMARY, { params: queryParams });
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch calls counter summary');
    }
  }
);

/** GET /dashboards/leads_comments */
export const getLeadsComments = createAsyncThunk(
  'dashboard/getLeadsComments',
  async (params = {}, { rejectWithValue }) => {
    try {
      const queryParams =
        params && typeof params === 'object' && Object.keys(params).length > 0
          ? { ...params }
          : undefined;
      const { data } = await api.get(DASHBOARD.LEADS_COMMENTS, { params: queryParams });
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch leads report');
    }
  }
);

/** GET /dashboards/total — pass date_from, date_to, team_id as query params when Apply filter is used */
export const getOverviewData = createAsyncThunk(
  'dashboard/getOverviewData',
  async (params = {}, { rejectWithValue }) => {
    try {
      const queryParams =
        params && typeof params === 'object' && Object.keys(params).length > 0
          ? { ...params }
          : undefined;
      const { data } = await api.get(DASHBOARD.TOTAL, { params: queryParams });
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch dashboard data');
    }
  }
);

/** Admin dashboard: teams + closers metrics. Params: { date_from?, date_to?, team_id? } */
export const getAdminDashboardData = createAsyncThunk(
  'dashboard/getAdminDashboardData',
  async (params = {}, { rejectWithValue }) => {
    try {
      const { data } = await api.get(DASHBOARD.ADMIN, { params });
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch admin dashboard');
    }
  }
);

/** Closer dashboard: personal metrics. Params: { date_from?, date_to? } */
export const getCloserDashboardData = createAsyncThunk(
  'dashboard/getCloserDashboardData',
  async (params = {}, { rejectWithValue }) => {
    try {
      const { data } = await api.get(DASHBOARD.CLOSER, { params });
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch closer dashboard');
    }
  }
);

export const getRecentActivities = createAsyncThunk(
  'dashboard/getRecentActivities',
  async ({ page = 1, pageSize = 5, search = '' }, { rejectWithValue }) => {
    try {
      const { data } = await api.get('/activity-logs', {
        params: { page, per_page: pageSize, search: search || undefined },
      });
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch recent activities');
    }
  }
);

export const getActivityLogById = createAsyncThunk(
  'dashboard/getActivityLogById',
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await api.get(ACTIVITY_LOGS.BY_ID(id));
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch activity log');
    }
  }
);

const initialState = {
  overview: null,
  recentActivities: null,
  activityLog: null,
  adminDashboard: null,
  closerDashboard: null,
  callsCounterSummary: null,
  leadsComments: null,
  loading: false,
  error: null,
};

const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    clearDashboardError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getOverviewData.fulfilled, (state, action) => {
        state.loading = false;
        state.overview = action.payload;
      })
      .addCase(getRecentActivities.fulfilled, (state, action) => {
        state.loading = false;
        state.recentActivities = action.payload;
      })
      .addCase(getActivityLogById.fulfilled, (state, action) => {
        state.loading = false;
        state.activityLog = action.payload;
      })
      .addCase(getAdminDashboardData.fulfilled, (state, action) => {
        state.loading = false;
        state.adminDashboard = action.payload;
      })
      .addCase(getCloserDashboardData.fulfilled, (state, action) => {
        state.loading = false;
        state.closerDashboard = action.payload;
      })
      .addCase(getCallsCounterSummary.fulfilled, (state, action) => {
        state.loading = false;
        state.callsCounterSummary = action.payload;
      })
      .addCase(getLeadsComments.fulfilled, (state, action) => {
        state.loading = false;
        state.leadsComments = action.payload;
      })
      .addMatcher(
        (a) =>
          [
            getOverviewData.pending,
            getRecentActivities.pending,
            getActivityLogById.pending,
            getAdminDashboardData.pending,
            getCloserDashboardData.pending,
            getCallsCounterSummary.pending,
            getLeadsComments.pending,
          ].some((t) => t.match(a)),
        (state) => {
          state.loading = true;
          state.error = null;
        }
      )
      .addMatcher(
        (a) =>
          [
            getOverviewData.rejected,
            getRecentActivities.rejected,
            getActivityLogById.rejected,
            getAdminDashboardData.rejected,
            getCloserDashboardData.rejected,
            getCallsCounterSummary.rejected,
            getLeadsComments.rejected,
          ].some((t) => t.match(a)),
        (state, action) => {
          state.loading = false;
          state.error = action.payload || 'Something went wrong';
        }
      );
  },
});

export const { clearDashboardError } = dashboardSlice.actions;
export default dashboardSlice.reducer;
